import { setContinueRegistry } from '@components/generic-modals';
import { showSidebar } from '@components/sidebar';

import { queryClient } from 'api/query-client';
import {
  getJourneyTravellerActionQueryKey,
  getTravellerJourneyActionsQueryKey,
} from 'api/traveller-actions/hooks';
import { SidebarContent as addToJourney } from 'ui/actions/add-to-journey/traveller-sidebar';
import { SidebarContent as anonymizeText } from 'ui/actions/anonymize-text/traveller-sidebar';
import { SidebarContent as askAIAgent } from 'ui/actions/ask-ai-agent/traveller-sidebar';
import { SidebarContent as askAI } from 'ui/actions/ask-ai/traveller-sidebar';
import { SidebarContent as breakLoop } from 'ui/actions/break-loop/traveller-sidebar';
import { SidebarContent as calendarEvent } from 'ui/actions/calendar-event/traveller-sidebar';
import { SidebarContent as code } from 'ui/actions/code/traveller-sidebar';
import { SidebarContent as comboSendForm } from 'ui/actions/combo-send-form/traveller-sidebar';
import { EditingProvider } from 'ui/actions/common';
import { SidebarContent as conditionV2 } from 'ui/actions/condition-v2/traveller-sidebar';
import { SidebarContent as condition } from 'ui/actions/condition/traveller-sidebar';
import { SidebarContent as convertDocument } from 'ui/actions/convert-document/traveller-sidebar';
import { SidebarContent as createDocument } from 'ui/actions/create-document/traveller-sidebar';
import { SidebarContent as extractText } from 'ui/actions/extract-text/traveller-sidebar';
import { SidebarContent as formatter } from 'ui/actions/formatter/traveller-sidebar';
import { SidebarContent as integration } from 'ui/actions/integration/traveller-sidebar';
import { SidebarContent as loop } from 'ui/actions/loop/traveller-sidebar';
import { SidebarContent as merge } from 'ui/actions/merge/traveller-sidebar';
import { SidebarContent as placeholder } from 'ui/actions/placeholder/traveller-sidebar';
import { SidebarContent as sendEmail } from 'ui/actions/send-email/traveller-sidebar';
import { SidebarContent as sendSMS } from 'ui/actions/send-sms/traveller-sidebar';
import { SidebarContent as journeyStart } from 'ui/actions/start/traveller-sidebar';
import { SidebarContent as updateFields } from 'ui/actions/update-fields/traveller-sidebar';
import { SidebarContent as updateTraveller } from 'ui/actions/update-traveller/traveller-sidebar';
import { SidebarContent as waitForAttributeChange } from 'ui/actions/wait-for-attribute-change/traveller-sidebar';
import { SidebarContent as waitForTime } from 'ui/actions/wait-for/traveller-sidebar';
import { SidebarContent as waitUntilFixed } from 'ui/actions/wait-until-fixed/traveller-sidebar';
import { SidebarContent as waitUntilRelative } from 'ui/actions/wait-until-relative/traveller-sidebar';
import { SidebarContent as webhook } from 'ui/actions/webhook/traveller-sidebar';
import { JourneyNodeBase } from 'ui/journey-builder/node-types/types';
import {
  FOCUS_NODE_DURATION,
  FOCUS_NODE_MAX_ZOOM,
  FOCUS_NODE_PADDING,
  getReactFlowInstance,
} from 'ui/journey-builder/show-action-sidebar';

const sidebarContentMap = {
  journey_start: journeyStart,
  send_email: sendEmail,
  combo_send_form: comboSendForm,
  ask_ai: askAI,
  ask_ai_agent: askAIAgent,
  condition,
  condition_v2: conditionV2,
  send_sms: sendSMS,
  create_document: createDocument,
  code,
  convert_document: convertDocument,
  calendar_event: calendarEvent,
  formatter,
  integration,
  webhook,
  journey_loop: loop,
  journey_merge: merge,
  break_loop: breakLoop,
  wait_for_time: waitForTime,
  wait_for_attribute_change: waitForAttributeChange,
  wait_until_fixed_date: waitUntilFixed,
  wait_until_relative_date: waitUntilRelative,
  update_fields: updateFields,
  add_to_journey: addToJourney,
  update_traveller: updateTraveller,
  placeholder,
  anonymize_text: anonymizeText,
  extract_text: extractText,
};

export const getHasTravellerActionSidebar = (nodeType?: JourneyNodeBase['type']) => {
  return nodeType && nodeType in sidebarContentMap;
};

export const showActionSidebar = (
  node: JourneyNodeBase,
  options?: { isEdit?: boolean; focusNode?: boolean },
) => {
  const hasSidebar = node.type in sidebarContentMap;
  if (!hasSidebar) return;

  const isEdit = options?.isEdit ?? false;
  const reactFlowInstance = getReactFlowInstance();
  if (options?.focusNode && reactFlowInstance) {
    reactFlowInstance.fitView({
      nodes: [{ id: node.id }],
      duration: FOCUS_NODE_DURATION,
      maxZoom: FOCUS_NODE_MAX_ZOOM,
      padding: FOCUS_NODE_PADDING,
    });
  }

  const SidebarComponent = sidebarContentMap[node.type as keyof typeof sidebarContentMap];
  setContinueRegistry({ onContinue: () => showActionSidebar(node, options), id: node.id });
  queryClient.removeQueries(getJourneyTravellerActionQueryKey(node.id));
  showSidebar({
    component: (props) => (
      <EditingProvider defaultIsEditing={isEdit}>
        <SidebarComponent {...props} />
      </EditingProvider>
    ),
    options: {
      variant: 'REGULAR',
      id: node.id,
      onHide: () => {
        queryClient.invalidateQueries(getTravellerJourneyActionsQueryKey());
      },
    },
    props: { shortId: node.id },
  });
};
